import React from "react";
import OELogo from "./OELogo";

/**
 * OpenEstimate full wordmark lockup.
 *
 * Renders: [OELogo mark] · "Open{Estimate}" · "PRECONSTRUCTION" auto-justified
 *
 * Layout: the text column uses CSS inline-grid with grid-template-columns:max-content.
 * The grid track width = max intrinsic width of all children = wordmark width.
 * The sublabel row inherits that same track width, and justify-content:space-between
 * distributes letters from the exact left edge to the exact right edge of the wordmark.
 * Zero JavaScript measurement — immune to font-load timing issues.
 *
 * Props:
 *   size         — wordmark font size in px (default 26). Logo height matches.
 *   wordColor    — color for "Open" (default currentColor)
 *   accentColor  — color for "Estimate" + E bars (default #4fd1c5 teal)
 *   subColor     — sublabel color (default #708898 muted)
 *   circleColor  — O ring color (default "white" — good on dark headers)
 *   markBarColor — E bar color on the logo mark, independent of accentColor.
 *                  Defaults to accentColor when not set.
 *   showSub      — show PRECONSTRUCTION sublabel (default true)
 *   onClick      — makes the whole lockup a button
 *   title        — tooltip
 *   style        — extra styles on outer container
 */

const DISPLAY = "'Jost', 'Inter', sans-serif";
const SUBLABEL = "PRECONSTRUCTION";

interface OEWordmarkProps {
  size?:         number;
  wordColor?:    string;
  accentColor?:  string;
  subColor?:     string;
  circleColor?:  string;
  markBarColor?: string;
  showSub?:      boolean;
  showMark?:     boolean;  // set false to render text only (no OELogo mark)
  onClick?:      () => void;
  title?:        string;
  style?:        React.CSSProperties;
}

const OEWordmark: React.FC<OEWordmarkProps> = ({
  size         = 26,
  wordColor    = "currentColor",
  accentColor  = "#4fd1c5",
  subColor     = "#708898",
  circleColor  = "white",
  markBarColor,
  showSub      = true,
  showMark     = true,
  onClick,
  title,
  style,
}) => {
  const subFontSize  = Math.max(7, Math.round(size * 0.28));
  const subMarginTop = Math.max(2, Math.round(size * 0.18));

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "flex-start",
        gap: Math.round(size * 0.42),
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        ...style,
      }}
      onClick={onClick}
      title={title}
    >
      {showMark && (
        <OELogo
          height={size}
          circleColor={circleColor}
          barColor={markBarColor ?? accentColor}
        />
      )}

      {/*
        inline-grid + max-content: the single column track is sized to the
        widest child (the wordmark span). Both rows share that exact width,
        so the sublabel row is always exactly as wide as "OpenEstimate".
      */}
      <div style={{ display: "inline-grid", gridTemplateColumns: "max-content", lineHeight: 1 }}>
        <span
          style={{
            fontFamily: DISPLAY,
            fontWeight: 600,
            fontSize: size,
            letterSpacing: "-0.01em",
            color: wordColor,
            whiteSpace: "nowrap",
          }}
        >
          Open<span style={{ color: accentColor }}>Estimate</span>
        </span>

        {showSub && (
          <div
            style={{
              marginTop: subMarginTop,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {SUBLABEL.split("").map((char, i) => (
              <span
                key={i}
                style={{
                  fontFamily: DISPLAY,
                  fontSize: subFontSize,
                  fontWeight: 500,
                  color: subColor,
                  lineHeight: 1,
                }}
              >
                {char}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OEWordmark;
